import { api } from '@/lib/api';

export type AppAnalyticsDashboard = {
  generatedAt: string;
  scope: 'hospital' | 'platform';
  hospitalId: string;
  executive: {
    activePatientsMau: number;
    activePatientsDau: number;
    dauMauRatioPct: number;
    repeatPatientPct: number;
    referralConversionPct: number;
    pointsLiabilityRupee: number;
    appUsersWithLogin: number;
    revenueFromAppUsers365: number;
    membershipActive: number;
  };
  engagement: {
    mau: number;
    dau: number;
    dauMauRatioPct: number;
    sessionsLast30d: number;
    avgSessionsPerMau: number;
    featureUsage: Array<{ screen: string; eventType: string; count: number }>;
    funnel: {
      login: number;
      viewedReport: number;
      bookedAppointment: number;
      redeemedPoints: number;
    };
  };
  loyalty: {
    pointsLiability: number;
    wallets: number;
    earnPointsLast30d: number;
    burnPointsLast30d: number;
    redemptionRatePct: number;
    totalPointsIssued: number;
    totalPointsRedeemed: number;
    avgPointsPerWallet: number;
    expiringPointsNext30d: number;
    liabilityRupee: number;
    tierDistribution: Array<{ tier: string; level?: string; count: number }>;
    tierRevenue: Array<{ tier: string; revenue: number; patients: number }>;
  };
  retention: {
    repeatVisitRates: Array<{ windowDays: number; ratePct: number; repeatPatients: number }>;
    specialtyRetention: Array<{
      specialisation: string;
      patients: number;
      repeatPatients: number;
      retentionPct: number;
    }>;
    cohortAnalysis: Array<{
      cohortMonth: string;
      cohortSize: number;
      returnedNext30d: number;
      retentionPct: number;
    }>;
  };
  referrals: {
    total: number;
    conversionPct: number;
    byStatus: Array<{ status: string; count: number }>;
    topReferrers: Array<{ patientId: string; name: string; uhid: string | null; count: number }>;
    superReferrers: Array<{ patientId: string; count: number }>;
  };
  revenue: {
    revenueAppUsers365: number;
    wallet: {
      pointsBalance: number;
      cashbackBalance: number;
      promoBalance: number;
      refundBalance: number;
      ledgerEntries30d: number;
    };
    membershipExpiring30d: number;
  };
  family: { linkedAccounts: number; poolingEnabled: boolean };
  campaigns: {
    inAppDeliveries30d: number;
    inAppRead30d: number;
    inAppOpenRatePct: number;
    whatsappByStatus: Array<{ status: string; count: number }>;
  };
  operations: {
    appointments30d: {
      total: number;
      diagnosed: number;
      confirmed: number;
      cancelled: number;
      cancellationRatePct: number;
      pendingRatePct: number;
      appBookingsTracked: number;
    };
    departmentAppAdoption: Array<{ department: string; bookings: number }>;
  };
  predictive: {
    churnRiskPatients: Array<{ patientId: string; name: string; lastVisit: string }>;
    highLtvPatients: Array<{ patientId: string; name: string; revenue365: number }>;
    followUpRiskAppointments: Array<{
      id: string;
      scheduledAt: string;
      status: string;
      patientName: string;
    }>;
    hooks: Record<string, string>;
  };
  benchmarking: { available: boolean; note: string };
  dailyTrends: {
    period: {
      year: number;
      month: number | null;
      label: string;
      granularity: 'day' | 'month';
    };
    days: number;
    appDownloads: Array<{ date: string; value: number }>;
    dau: Array<{ date: string; value: number }>;
    pointsEarned: Array<{ date: string; value: number }>;
    pointsRedeemed: Array<{ date: string; value: number }>;
    revenue: Array<{ date: string; value: number }>;
    familyMembersAdded: Array<{ date: string; value: number }>;
  };
};

export type AppAnalyticsQuery = {
  year: number;
  /** 1–12 for a month; pass null for full-year monthly view */
  month: number | null;
};

export const appAnalyticsApi = {
  getDashboard: (params: AppAnalyticsQuery) =>
    api
      .get('/api/loyalty/app-analytics', {
        params: {
          year: params.year,
          month: params.month == null ? 'all' : params.month,
        },
      })
      .then((r) => r.data.data as AppAnalyticsDashboard),
  getPlatformSummary: () =>
    api
      .get('/api/loyalty/app-analytics/platform')
      .then((r) => r.data.data as { scope: string; activeHospitals: number; eventsLast30d: number; platformMau: number }),
};
